import { keepPreviousData, useInfiniteQuery } from '@tanstack/react-query';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useSearchParams } from 'react-router-dom';

import { Answer } from '@/components/common/Answer';
import { AsOfStrip } from '@/components/common/AsOfStrip';
import { ExplainerCard } from '@/components/common/ExplainerCard';
import { ExportButton } from '@/components/common/ExportButton';
import { FieldGuide } from '@/components/common/FieldGuide';
import { EmptyState, ErrorMessage, SkeletonLoader } from '@/components/common/StateViews';
import { useAutoLoadMore } from '@/hooks/useAutoLoadMore';
import { ansCaseParams, fetchAnsCases } from '@/services/ans';
import type { AnsCaseRow, AnsFilterOption } from '@/services/types';

const PAGE_SIZE = 50;
const CNPJ_DIGITS = 14;

/**
 * Every penalty in the ANS snapshot, filtered.
 *
 * Every case here carries a first-instance decision; what varies is whether
 * the Diretoria Colegiada reviewed it, whether it was archived, and what the
 * GRU says. The five status axes above the table are five different processes
 * and disagree by design, so no single "resolved" figure is shown.
 *
 * Every filter seeds from the URL, because the overview, the operator profile
 * and the NIP funnel link here with a filter already applied. Rows accumulate
 * as the reader scrolls; `keepPreviousData` keeps the previous total on screen
 * while a narrowed query is in flight, and `data-stale` says so.
 */
export function AnsCasesPage() {
  const { t, i18n } = useTranslation(['ans', 'common']);
  const [seed] = useSearchParams();
  const [draft, setDraft] = useState(seed.get('query') ?? '');
  const [query, setQuery] = useState<string | null>(seed.get('query') || null);
  const [track, setTrack] = useState(seed.get('track') ?? '');
  const [object, setObject] = useState(seed.get('object') ?? '');
  const [boardOutcome, setBoardOutcome] = useState(seed.get('board_outcome') ?? '');
  const [trajectory, setTrajectory] = useState(seed.get('trajectory') ?? '');
  const [year, setYear] = useState(seed.get('year') ?? '');
  const [creditStatus, setCreditStatus] = useState(seed.get('credit_status') ?? '');
  const [caseStatus, setCaseStatus] = useState(seed.get('case_status') ?? '');
  const [dividaAtiva, setDividaAtiva] = useState(seed.get('divida_ativa') ?? '');
  const [entity, setEntity] = useState(seed.get('entity') ?? '');

  const filters = {
    query,
    track: track || null,
    object: object || null,
    board_outcome: boardOutcome || null,
    trajectory: trajectory || null,
    year: year ? Number(year) : null,
    credit_status: creditStatus || null,
    case_status: caseStatus || null,
    divida_ativa: dividaAtiva === '' ? null : dividaAtiva === 'true',
    entity: entity || null,
  };

  const {
    data: paged,
    isPending,
    error,
    refetch,
    isPlaceholderData,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
  } = useInfiniteQuery({
    queryKey: ['ans', 'cases', filters],
    queryFn: ({ pageParam }) => fetchAnsCases({ ...filters, limit: PAGE_SIZE, offset: pageParam }),
    initialPageParam: 0,
    getNextPageParam: (lastPage, allPages) => {
      const loaded = allPages.reduce((n, page) => n + page.items.length, 0);
      return loaded < lastPage.total ? loaded : undefined;
    },
    placeholderData: keepPreviousData,
  });

  const data = paged?.pages[0];
  const items = paged?.pages.flatMap((page) => page.items) ?? [];

  const { sentinelRef, scrollRef, loadMore } = useAutoLoadMore({
    hasNextPage,
    isFetchingNextPage,
    fetchNextPage,
  });

  const number = new Intl.NumberFormat(i18n.language);
  const currency = new Intl.NumberFormat(i18n.language, {
    style: 'currency',
    currency: 'BRL',
    maximumFractionDigits: 0,
  });
  const percent = new Intl.NumberFormat(i18n.language, {
    style: 'percent',
    maximumFractionDigits: 1,
  });

  const options = data?.options ?? {};
  const outcomeLabel = (option: AnsFilterOption) =>
    t(`ans:overview.outcomes.${option.value}`, { defaultValue: option.value });

  return (
    <div className="space-y-4 p-6">
      <header className="space-y-1">
        <h1 className="text-lg font-semibold text-foreground">{t('ans:cases.title')}</h1>
        <p className="text-sm text-muted-foreground">{t('ans:cases.subtitle')}</p>
      </header>

      <ExplainerCard i18nKey="ans:cases.explainer" />

      <div className="flex justify-end">
        <ExportButton path={`/api/v1/ans/export/cases?${ansCaseParams(filters).toString()}`} />
      </div>

      {data ? <AsOfStrip asOf={data.as_of} /> : null}

      <form
        className="flex flex-wrap items-end gap-3 rounded-lg border bg-card p-3"
        onSubmit={(event) => {
          event.preventDefault();
          setQuery(draft.trim() || null);
        }}
      >
        <div className="min-w-[16rem] flex-1 space-y-1">
          <label htmlFor="ans-case-query" className="text-xs font-medium text-foreground">
            {t('ans:cases.search.label')}
          </label>
          <input
            id="ans-case-query"
            value={draft}
            onChange={(event) => setDraft(event.target.value)}
            placeholder={t('ans:cases.search.placeholder')}
            className="w-full rounded-md border bg-background px-3 py-1.5 text-sm text-foreground"
          />
        </div>
        <Select
          id="ans-track"
          label={t('ans:cases.filters.track')}
          any={t('ans:cases.filters.any')}
          value={track}
          onChange={setTrack}
          options={options.track}
        />
        <Select
          id="ans-object"
          label={t('ans:cases.filters.object')}
          any={t('ans:cases.filters.any')}
          value={object}
          onChange={setObject}
          options={options.object}
        />
        <Select
          id="ans-board-outcome"
          label={t('ans:cases.filters.boardOutcome')}
          any={t('ans:cases.filters.any')}
          value={boardOutcome}
          onChange={setBoardOutcome}
          options={[
            {
              value: 'any',
              cases: data?.status_axes.value?.board_reviewed ?? 0,
            },
            {
              value: 'none',
              cases: data
                ? (data.status_axes.value?.total_cases ?? 0) -
                  (data.status_axes.value?.board_reviewed ?? 0)
                : 0,
            },
            ...(options.board_outcome ?? []),
          ]}
          render={(option) =>
            option.value === 'any' || option.value === 'none'
              ? t(`ans:cases.filters.boardOutcome_${option.value}`)
              : outcomeLabel(option)
          }
        />
        <Select
          id="ans-trajectory"
          label={t('ans:cases.filters.trajectory')}
          any={t('ans:cases.filters.any')}
          value={trajectory}
          onChange={setTrajectory}
          options={options.trajectory}
          render={(option) =>
            t(`ans:cases.trajectories.${option.value}`, {
              defaultValue: option.value,
            })
          }
        />
        <Select
          id="ans-year"
          label={t('ans:cases.filters.year')}
          any={t('ans:cases.filters.any')}
          value={year}
          onChange={setYear}
          options={options.year}
        />
        <Select
          id="ans-credit"
          label={t('ans:cases.filters.creditStatus')}
          any={t('ans:cases.filters.any')}
          value={creditStatus}
          onChange={setCreditStatus}
          options={options.credit_status}
        />
        <Select
          id="ans-status"
          label={t('ans:cases.filters.caseStatus')}
          any={t('ans:cases.filters.any')}
          value={caseStatus}
          onChange={setCaseStatus}
          options={options.case_status}
        />
        <div className="space-y-1">
          <label htmlFor="ans-da" className="block text-xs font-medium text-foreground">
            {t('ans:cases.filters.dividaAtiva')}
          </label>
          <select
            id="ans-da"
            value={dividaAtiva}
            onChange={(event) => setDividaAtiva(event.target.value)}
            className="rounded-md border bg-background px-2 py-1.5 text-sm text-foreground"
          >
            <option value="">{t('ans:cases.filters.any')}</option>
            <option value="true">{t('ans:cases.filters.dividaAtiva_yes')}</option>
            <option value="false">{t('ans:cases.filters.dividaAtiva_no')}</option>
          </select>
        </div>
        <button
          type="submit"
          className="rounded-md bg-primary px-3 py-1.5 text-sm font-medium text-primary-foreground"
        >
          {t('ans:cases.search.submit')}
        </button>
      </form>

      {entity ? (
        <div className="flex items-center gap-2 text-xs text-muted-foreground" data-entity-chip>
          <span>{t('ans:cases.filters.entityChip', { cnpj: entity })}</span>
          <button
            type="button"
            onClick={() => setEntity('')}
            className="rounded border px-2 py-0.5 text-foreground"
          >
            {t('ans:cases.filters.clear')}
          </button>
        </div>
      ) : null}

      {data ? (
        <div className="grid gap-4 lg:grid-cols-2">
          <section className="rounded-lg border bg-card p-4" data-status-axes>
            <h2 className="mb-2 text-sm font-semibold text-foreground">
              {t('ans:cases.axes.title')}
            </h2>
            <Answer answer={data.status_axes} help={t('ans:cases.axes.help')}>
              {(axes) => (
                <dl className="grid grid-cols-2 gap-2 text-sm sm:grid-cols-3">
                  {(
                    [
                      [
                        'boardReviewed',
                        axes.board_reviewed,
                        ansCasesHref({ board_outcome: 'any' }),
                      ],
                      ['archived', axes.archived, null],
                      ['paid', axes.paid, ansCasesHref({ credit_status: 'Pago' })],
                      ['dividaAtiva', axes.divida_ativa, ansCasesHref({ divida_ativa: true })],
                      ['judiciallySuspended', axes.judicially_suspended, null],
                    ] as const
                  ).map(([key, value, href]) => (
                    <div key={key} className="rounded-md border bg-muted/30 p-2" data-axis={key}>
                      <dt className="text-xs text-muted-foreground">
                        {t(`ans:cases.axes.${key}`)}
                      </dt>
                      <dd className="font-semibold tabular-nums text-foreground">
                        {href ? (
                          <Link
                            to={href}
                            className="text-primary underline-offset-2 hover:underline"
                          >
                            {number.format(value)}
                          </Link>
                        ) : (
                          number.format(value)
                        )}
                        <span className="ml-1 text-xs font-normal text-muted-foreground">
                          ({percent.format(axes.total_cases ? value / axes.total_cases : 0)})
                        </span>
                      </dd>
                    </div>
                  ))}
                </dl>
              )}
            </Answer>
            <div className="mt-3">
              <FieldGuide i18nKey="ans:cases.guide.axes" />
            </div>
          </section>

          <section className="rounded-lg border bg-card p-4" data-concentration>
            <h2 className="mb-2 text-sm font-semibold text-foreground">
              {t('ans:cases.concentration.title')}
            </h2>
            <Answer answer={data.concentration} help={t('ans:cases.concentration.help')}>
              {(c) => (
                <div className="space-y-2 text-sm">
                  <p className="text-xs text-muted-foreground">
                    {t('ans:cases.concentration.summary', {
                      entities: number.format(c.entities),
                      cases: number.format(c.cases),
                      single: percent.format(c.single_case_share),
                    })}
                  </p>
                  <ul className="grid grid-cols-5 gap-2">
                    {c.bands.map((band) => (
                      <li
                        key={band.label}
                        className="rounded-md border bg-muted/30 p-2"
                        data-band={band.label}
                      >
                        <div className="text-xs text-muted-foreground">
                          {t('ans:cases.concentration.band', {
                            band: band.label,
                          })}
                        </div>
                        <div className="font-semibold tabular-nums text-foreground">
                          {number.format(band.entities)}
                        </div>
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </Answer>
          </section>
        </div>
      ) : null}

      {isPending ? (
        <SkeletonLoader rows={8} />
      ) : error ? (
        <ErrorMessage message={error.message} onRetry={() => void refetch()} />
      ) : items.length === 0 ? (
        <EmptyState hint={t('ans:cases.empty')} />
      ) : (
        <section className="rounded-lg border bg-card" data-stale={isPlaceholderData || undefined}>
          <div className="flex items-center justify-between px-4 py-2 text-xs text-muted-foreground">
            <span data-total={data?.total}>
              {t('ans:cases.count', {
                loaded: number.format(items.length),
                total: number.format(data?.total ?? 0),
              })}
            </span>
          </div>
          <div ref={scrollRef} className="max-h-[70vh] overflow-auto">
            <table className="w-full text-sm">
              <thead className="sticky top-0 z-10 bg-muted text-left text-xs text-muted-foreground">
                <tr>
                  <th className="px-3 py-2 font-medium">{t('ans:cases.columns.demand')}</th>
                  <th className="px-3 py-2 font-medium">{t('ans:cases.columns.operator')}</th>
                  <th className="px-3 py-2 font-medium">{t('ans:cases.columns.track')}</th>
                  <th className="px-3 py-2 font-medium">{t('ans:cases.columns.firstDecision')}</th>
                  <th className="px-3 py-2 font-medium">{t('ans:cases.columns.boardOutcome')}</th>
                  <th className="px-3 py-2 text-right font-medium">
                    {t('ans:cases.columns.original')}
                  </th>
                  <th className="px-3 py-2 text-right font-medium">
                    {t('ans:cases.columns.final')}
                  </th>
                  <th className="px-3 py-2 font-medium">{t('ans:cases.columns.creditStatus')}</th>
                </tr>
              </thead>
              <tbody>
                {items.map((row) => (
                  <CaseTableRow key={row.notice_number} row={row} currency={currency} />
                ))}
              </tbody>
            </table>
            <div ref={sentinelRef} />
          </div>
          {hasNextPage ? (
            <div className="border-t px-4 py-2 text-center">
              <button
                type="button"
                onClick={loadMore}
                disabled={isFetchingNextPage}
                className="text-sm text-primary underline-offset-2 hover:underline"
              >
                {isFetchingNextPage ? t('common:state.loading') : t('ans:cases.loadMore')}
              </button>
            </div>
          ) : null}
        </section>
      )}
    </div>
  );
}

export function CaseTableRow({ row, currency }: { row: AnsCaseRow; currency: Intl.NumberFormat }) {
  const { t } = useTranslation(['ans']);
  return (
    <tr className="border-t align-top" data-demand={row.notice_number}>
      <td className="px-3 py-2">
        <Link
          to={`/ans/c/${row.notice_number}`}
          className="font-medium tabular-nums text-primary underline-offset-4 hover:underline"
        >
          {row.notice_number}
        </Link>
        {row.process_number ? (
          <div className="text-xs text-muted-foreground">{row.process_number}</div>
        ) : null}
      </td>
      <td className="px-3 py-2">
        {row.entity_cnpj && row.entity_cnpj.length === CNPJ_DIGITS ? (
          <Link
            to={`/ans/e/${row.entity_cnpj}`}
            className="line-clamp-2 text-primary underline-offset-4 hover:underline"
          >
            {row.entity_name ?? row.entity_cnpj}
          </Link>
        ) : (
          <span className="text-muted-foreground">{row.entity_name ?? '—'}</span>
        )}
      </td>
      <td className="px-3 py-2 text-muted-foreground">
        {row.track ?? '—'}
        {row.object ? <div className="text-xs">{row.object}</div> : null}
      </td>
      <td className="px-3 py-2 tabular-nums text-muted-foreground">
        {row.first_decision_date ?? '—'}
      </td>
      <td className="px-3 py-2 text-muted-foreground">
        {row.board_outcome ?? <span className="italic">{t('ans:cases.notReviewed')}</span>}
      </td>
      <td className="px-3 py-2 text-right tabular-nums text-muted-foreground">
        {row.original_amount === null ? '—' : currency.format(row.original_amount)}
      </td>
      <td className="px-3 py-2 text-right tabular-nums text-foreground">
        {row.final_amount === null ? '—' : currency.format(row.final_amount)}
        {row.trajectory && row.trajectory !== 'MAINTAINED' ? (
          <div className="text-xs text-muted-foreground">
            {t(`ans:cases.trajectories.${row.trajectory}`, {
              defaultValue: row.trajectory,
            })}
          </div>
        ) : null}
      </td>
      <td className="px-3 py-2 text-muted-foreground">
        {row.credit_status ?? '—'}
        {row.divida_ativa ? <div className="text-xs">{t('ans:cases.dividaAtiva')}</div> : null}
      </td>
    </tr>
  );
}

function ansCasesHref(filters: Parameters<typeof ansCaseParams>[0]): string {
  const params = ansCaseParams(filters).toString();
  return params ? `/ans/cases?${params}` : '/ans/cases';
}

function Select({
  id,
  label,
  any,
  value,
  onChange,
  options,
  render,
}: {
  id: string;
  label: string;
  any: string;
  value: string;
  onChange: (value: string) => void;
  options?: AnsFilterOption[];
  render?: (option: AnsFilterOption) => string;
}) {
  const { i18n } = useTranslation();
  const number = new Intl.NumberFormat(i18n.language);
  return (
    <div className="space-y-1">
      <label htmlFor={id} className="block text-xs font-medium text-foreground">
        {label}
      </label>
      <select
        id={id}
        value={value}
        onChange={(event) => onChange(event.target.value)}
        className="max-w-[13rem] rounded-md border bg-background px-2 py-1.5 text-sm text-foreground"
      >
        <option value="">{any}</option>
        {(options ?? []).map((option) => (
          <option key={option.value} value={option.value}>
            {render ? render(option) : option.value} ({number.format(option.cases)})
          </option>
        ))}
      </select>
    </div>
  );
}
